import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { articleService, Article, ArticleListResponse } from '../../services/article.service';

const BlogPage: React.FC = () => {
  const { t } = useTranslation();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(12);
  const [hasMore, setHasMore] = useState<boolean>(true);

  // Ref for intersection observer
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Fetch articles from API
  const fetchArticles = useCallback(
    async (page: number, append: boolean = false) => {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const response: ArticleListResponse = await articleService.getPublicArticles({ 
          page, 
          limit: pageSize 
        });

        if (append) {
          setArticles((prev) => [...prev, ...response.articles]);
        } else {
          setArticles(response.articles);
        }

        setTotal(response.pagination.total);
        // Update hasMore based on current page and total pages
        setHasMore(page < response.pagination.total_pages);
      } catch (err) {
        console.error('Error fetching articles:', err);
        setError('Không thể tải bài viết');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [pageSize]
  );

  // Initial load of articles
  useEffect(() => {
    setCurrentPage(1);
    fetchArticles(1, false);
  }, [fetchArticles]);

  // Load more articles when scrolling
  const loadMoreArticles = useCallback(() => {
    if (loadingMore || !hasMore) return;
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchArticles(nextPage, true);
  }, [currentPage, fetchArticles, hasMore, loadingMore]);

  // Setup intersection observer for infinite scroll
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '100px', // Add some margin to trigger earlier
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
        loadMoreArticles();
      }
    }, options);

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [hasMore, loadMoreArticles, loadingMore, loading]);

  // Calculate read time based on content length
  const calculateReadTime = (content: string) => {
    const wordsPerMinute = 200;
    const wordCount = content.split(' ').length;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return `${minutes} phút`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark"
          >
            Thử lại
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold text-primary mb-2">Blog Ume Fruits</h1>
          <p className="text-gray-600">Chia sẻ kiến thức về trái cây và sức khỏe</p>
        </div>
      </div>

      {/* Blog Posts Grid */}
      <div className="container mx-auto px-4 py-8">
        {/* Articles count info */}
        <div className="mb-6">
          <p className="text-gray-500">
            {loading && currentPage === 1
              ? 'Đang tải bài viết...'
              : `Hiển thị ${articles.length} trong số ${total} bài viết`}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles.map((article) => (
            <Link
              key={article.id}
              to={`/blog/${article.id}`}
              className="block bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 hover:scale-105"
            >
              <article>
                              {/* Image - using thumbnail from API */}
              <div className="relative h-48 overflow-hidden">
                {article.images && article.images.length > 0 ? (
                  <img
                    src={article.images[0]}
                    alt={article.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-4xl mb-2">📝</div>
                      <div className="text-sm text-gray-500">Bài viết</div>
                    </div>
                  </div>
                )}
                {article.tags && article.tags.length > 0 && (
                  <div className="absolute top-3 left-3">
                    <span className="bg-primary text-white text-xs px-2 py-1 rounded-full">
                      {article.tags[0]}
                    </span>
                  </div>
                )}
              </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-3">
                    <span>{article.author}</span>
                    <span className="mx-2">•</span>
                    <span>{formatDate(article.publishedAt)}</span>
                    <span className="mx-2">•</span>
                    <span>{calculateReadTime(article.content)}</span>
                  </div>

                  <h2 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-primary transition-colors duration-200">
                    {article.title}
                  </h2>

                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {article.excerpt}
                  </p>

                  <div className="inline-flex items-center text-primary font-medium group-hover:text-primary-dark transition-colors duration-200">
                    Đọc thêm
                    <svg
                      className="ml-1 w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </div>
              </article>
            </Link>
          ))}
        </div>

        {/* Load More Indicator */}
        <div ref={loadMoreRef} className="mt-8 py-4 text-center">
          {loadingMore && (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-2 text-gray-500">
                Đang tải thêm bài viết...
              </span>
            </div>
          )}
          {!hasMore && articles.length > 0 && (
            <p className="text-gray-500">Đã hiển thị tất cả bài viết</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogPage;
